import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { courseApi, paymentApi } from '../../services/firebaseApi';
import toast from 'react-hot-toast';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

export default function CourseJoin() {
  const [courseCode, setCourseCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [courseInfo, setCourseInfo] = useState(null);

  const { currentUser, userRole, isInstructorAnywhere, isSchoolAdministrator, hasSemesterAccess, refreshUser } = useAuth();

  // Handle payment return URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const returnedCode = params.get('code');

    if (paymentStatus === 'success') {
      toast.success('Payment successful! You can now join the course.');
      if (returnedCode) {
        setCourseCode(returnedCode);
      }
      // Refresh user doc to pick up semesterAccess written by webhook
      if (refreshUser) {
        refreshUser();
      }
      // Clean up URL params
      window.history.replaceState({}, '', '/join');
    } else if (paymentStatus === 'cancelled') {
      toast('Payment was cancelled. You can try again when ready.', { icon: '\u2139\ufe0f' });
      if (returnedCode) {
        setCourseCode(returnedCode);
      }
      window.history.replaceState({}, '', '/join');
    } else if (returnedCode) {
      setCourseCode(returnedCode);
      window.history.replaceState({}, '', '/join');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const needsPayment = () => {
    if (userRole === 'admin') return false;
    if (isSchoolAdministrator) return false;
    if (isInstructorAnywhere) return false;
    return !hasSemesterAccess;
  };

  const handleCourseCodeSubmit = async (e) => {
    e.preventDefault();
    console.log('CourseJoin: Form submitted');

    if (!courseCode.trim()) {
      toast.error('Please enter a course code');
      return;
    }

    // Check if user is logged in first
    if (!currentUser) {
      console.log('CourseJoin: User not logged in');
      toast.error('Please log in first to join a course');
      localStorage.setItem('pendingCourseJoin', JSON.stringify({
        courseCode: courseCode.trim().toUpperCase()
      }));
      window.location.href = '/login';
      return;
    }

    setLoading(true);

    try {
      // Find the course first
      const course = await courseApi.getCourseByCode(courseCode.trim().toUpperCase());
      console.log('CourseJoin: Course found:', course.title);
      setCourseInfo(course);

      // Payment gate: student roles without semester access must pay first
      if (needsPayment()) {
        console.log('CourseJoin: Redirecting to Stripe checkout');
        toast('Redirecting to payment...', { icon: '\ud83d\udcb3' });
        const { url } = await paymentApi.createCheckoutSession(
          currentUser.id,
          currentUser.email,
          courseCode.trim().toUpperCase()
        );
        window.location.href = url;
        return;
      }

      await courseApi.joinCourse(currentUser.id, course.id, courseCode.trim().toUpperCase(), 'student');

      toast.success(`Successfully requested to join ${course.title}! Your request is pending instructor approval.`);

      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);

    } catch (error) {
      console.error('Error joining course:', error);
      if (error.message.includes('Course not found')) {
        toast.error('Course not found. Please check your course code.');
      } else if (error.message.includes('already exists')) {
        toast.error('You are already a member of this course.');
      } else {
        toast.error('Failed to join course. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="hero-mesh min-h-screen flex items-center justify-center p-4">
      <div className="relative z-10 max-w-md w-full space-y-8">
        <div className="animate-fade-up text-center">
          <div className="mx-auto h-12 w-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center">
            <AcademicCapIcon className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "'DM Serif Display', serif" }}>
            Join Your Course
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            Enter your course code to join
          </p>
        </div>

        <form onSubmit={handleCourseCodeSubmit} className="animate-fade-up animate-delay-1 bg-white p-8 rounded-xl shadow-2xl shadow-black/25 space-y-6">
          <div>
            <label htmlFor="courseCode" className="block text-sm font-medium text-gray-700 mb-2">
              Course Code
            </label>
            <input
              id="courseCode"
              name="courseCode"
              type="text"
              required
              value={courseCode}
              onChange={(e) => setCourseCode(e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-mono tracking-widest"
              placeholder="MT-SP25"
              maxLength={10}
            />
            <p className="mt-1 text-xs text-gray-500">
              Ask your instructor for the course code
            </p>
          </div>

          {/* Payment info for users without semester access */}
          {currentUser && needsPayment() && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start">
                <CreditCardIcon className="h-5 w-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <strong>Semester access required — $49</strong>
                  <p className="mt-1">
                    Student access requires a one-time $49 semester fee. You'll be redirected to a secure checkout page. Promo codes can be applied at checkout.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Semester access confirmed */}
          {currentUser && hasSemesterAccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="text-sm text-green-800">
                Semester access active — you're all set to join.
              </div>
            </div>
          )}

          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-sm text-blue-800">
              <strong>Note:</strong>
              <p className="mt-1">
                Your request to join this course will need to be approved by an instructor.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : needsPayment() ? (
              <>
                Continue to Payment
                <CreditCardIcon className="ml-2 h-4 w-4" />
              </>
            ) : (
              <>
                Join Course
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </>
            )}
          </button>
        </form>

        <div className="animate-fade-up animate-delay-2 text-center">
          <p className="text-xs text-gray-400">
            Don't have a course code? Contact your instructor or administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
